// client/src/components/pages/LexiStar/Replay/ReplayZones.tsx
//
// ST-00272: the tenant's zones on the Replay map, the way Live View draws them (a light
// fill and a solid outline in the zone type's colour), as they were at the replay clock
// (./replayZones). Self-contained so ReplayMap only places three things:
//   - `layer`  inside the map, first among its children: the zone source and layers, kept
//              under the track, the plan and the stops (moved below the first `rp-` layer
//              whenever the style changes), and the asset marker (a DOM marker) above all;
//   - `available` / `on` / `toggle` for the map's "Zones" button (next to Corridor / Follow);
//   - `legend` for the legend under the map: one chip per zone type present, which also
//              switches that type on or off.
// Names: on hover (or a tap on a phone) at any zoom; drawn on the map from ZONE_LABEL_ZOOM,
// just above each zone's top edge (clear of the trucks inside it).
import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Layer, Marker, Source, useMap } from 'react-map-gl/maplibre';
import type { Map as MaplibreMap, MapLayerMouseEvent } from 'maplibre-gl';
import type { FeatureCollection, Polygon } from 'geojson';
import { timeBounds, type ReplayData } from './replayLogic';
import {
  ZONE_LABEL_ZOOM,
  fetchZonesInForce,
  loadZonePrefs,
  presentZoneTypes,
  saveZonePrefs,
  shapesAt,
  shapesCollection,
  zoneShapes,
  zoneWindow,
  type ZoneInForce,
  type ZonePrefs,
  type ZoneShape,
} from './replayZones';

const FILL_ID = 'rp-zones-fill';
const LINE_ID = 'rp-zones-line';
const ZONE_LAYER_IDS = [FILL_ID, LINE_ID];
// In-map labels keep literal colours: the basemaps do not follow the app theme.
const LABEL_BG = 'rgba(15, 23, 42, 0.86)';
const LABEL_INK = '#ffffff';

/** Move the zone layers under the first other replay layer (track, plan, corridor, stops). */
export function keepZonesBelow(map: Pick<MaplibreMap, 'getStyle' | 'moveLayer'>): void {
  let layers: { id: string }[];
  try {
    layers = map.getStyle()?.layers || [];
  } catch {
    return; // style not ready
  }
  const first = layers.find((l) => l.id.startsWith('rp-') && !ZONE_LAYER_IDS.includes(l.id));
  if (!first) return;
  for (const id of ZONE_LAYER_IDS) {
    const order = layers.map((l) => l.id);
    const i = order.indexOf(id);
    if (i > order.indexOf(first.id)) {
      try {
        map.moveLayer(id, first.id);
        layers = map.getStyle()?.layers || [];
      } catch {
        return;
      }
    }
  }
}

function ZoneLabel({ name, sub, color }: { name: string; sub?: string; color: string }) {
  return (
    <div
      className="flex items-center gap-1.5 max-w-[220px] rounded-md px-2 py-1 text-[11px] font-medium shadow-lg pointer-events-none"
      style={{ background: LABEL_BG, color: LABEL_INK }}
    >
      <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
      <span className="truncate">{name}</span>
      {sub && <span className="opacity-75 whitespace-nowrap">· {sub}</span>}
    </div>
  );
}

interface HoverState {
  lng: number;
  lat: number;
  key: string;
}

interface ZoneLayerProps {
  fc: FeatureCollection<Polygon>;
  shapes: ZoneShape[];
}

/** The zone source + layers and their labels (memoised: the replay clock does not re-render it). */
export const ReplayZoneLayer = memo(function ReplayZoneLayer({ fc, shapes }: ZoneLayerProps) {
  const map = useMap().current?.getMap() as MaplibreMap | undefined;
  const [zoom, setZoom] = useState<number>(() => {
    try {
      return map?.getZoom() ?? 0;
    } catch {
      return 0;
    }
  });
  const [hover, setHover] = useState<HoverState | null>(null);

  useEffect(() => {
    if (!map) return undefined;
    const keep = () => keepZonesBelow(map);
    const onZoom = () => setZoom(map.getZoom());
    const onMove = (e: MapLayerMouseEvent) => {
      const key = e.features?.[0]?.properties?.key as string | undefined;
      if (key) setHover({ lng: e.lngLat.lng, lat: e.lngLat.lat, key });
    };
    const onLeave = () => setHover(null);
    // A tap on a phone: the same label; a tap outside a zone clears it.
    const onMapClick = (e: MapLayerMouseEvent) => {
      let hit: { properties?: { key?: string } | null }[] = [];
      try {
        hit = map.getLayer(FILL_ID) ? map.queryRenderedFeatures(e.point, { layers: [FILL_ID] }) : [];
      } catch {
        hit = [];
      }
      const key = hit[0]?.properties?.key;
      setHover(key ? { lng: e.lngLat.lng, lat: e.lngLat.lat, key } : null);
    };
    keep();
    map.on('styledata', keep);
    map.on('idle', keep);
    map.on('zoomend', onZoom);
    map.on('mousemove', FILL_ID, onMove);
    map.on('mouseleave', FILL_ID, onLeave);
    map.on('click', onMapClick);
    return () => {
      map.off('styledata', keep);
      map.off('idle', keep);
      map.off('zoomend', onZoom);
      map.off('mousemove', FILL_ID, onMove);
      map.off('mouseleave', FILL_ID, onLeave);
      map.off('click', onMapClick);
    };
  }, [map]);

  const hovered = hover ? shapes.find((s) => s.key === hover.key) : undefined;
  const showNames = zoom >= ZONE_LABEL_ZOOM;

  return (
    <>
      <Source id="rp-zones" type="geojson" data={fc}>
        <Layer
          id={FILL_ID}
          type="fill"
          paint={{ 'fill-color': ['get', 'color'], 'fill-opacity': 0.15 }}
        />
        <Layer id={LINE_ID} type="line" paint={{ 'line-color': ['get', 'color'], 'line-width': 2 }} />
      </Source>
      {showNames &&
        shapes.map((s) =>
          hovered?.key === s.key ? null : (
            <Marker
              key={`zl-${s.key}`}
              longitude={s.labelAt[0]}
              latitude={s.labelAt[1]}
              anchor="bottom"
              offset={[0, -2]}
              style={{ pointerEvents: 'none' }}
            >
              <ZoneLabel name={s.zone.name} color={s.color} />
            </Marker>
          )
        )}
      {hover && hovered && (
        <Marker longitude={hover.lng} latitude={hover.lat} anchor="bottom" offset={[0, -8]} style={{ pointerEvents: 'none', zIndex: 2 }}>
          <ZoneLabel
            name={hovered.zone.name}
            sub={[hovered.typeLabel, hovered.zone.location_name].filter(Boolean).join(' · ')}
            color={hovered.color}
          />
        </Marker>
      )}
    </>
  );
});

export interface ReplayZones {
  /** There are zones to draw in this replay (the Zones button shows). */
  available: boolean;
  on: boolean;
  toggle: () => void;
  /** Inside the map, before the other replay sources. */
  layer: ReactNode;
  /** A legend item: the zone-type chips (null when there is nothing to show). */
  legend: ReactNode;
}

/** A replay window in ms (ST-00277: the multi-truck Replay passes its window, not one track). */
export interface ZoneWindowBounds {
  start: number;
  end: number;
}

/**
 * The zones of the replay window, at the replay clock `at` (ms; null before it is known). The
 * window is one replay's time bounds, or (ST-00277, several trucks) the window itself.
 */
export default function useReplayZones(source: ReplayData | ZoneWindowBounds, at: number | null): ReplayZones {
  const [prefs, setPrefs] = useState<ZonePrefs>(loadZonePrefs);
  const [zones, setZones] = useState<ZoneInForce[]>([]);

  const replay = 'track' in source ? source : null;
  const span = 'track' in source ? null : source;
  const firstT = replay?.track[0]?.t;
  const lastT = replay?.track[replay.track.length - 1]?.t;
  const bounds = useMemo(
    () => (replay ? timeBounds(replay) : span ? { start: span.start, end: span.end } : null),
    // The bounds follow the first load: detail splicing keeps the ends of the track.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [replay?.events, replay?.stage_bands, firstT, lastT, span?.start, span?.end]
  );
  const win = zoneWindow(bounds);
  const winFrom = win?.[0] ?? null;
  const winTo = win?.[1] ?? null;

  useEffect(() => {
    if (winFrom === null || winTo === null) {
      setZones([]);
      return undefined;
    }
    let alive = true;
    fetchZonesInForce(winFrom, winTo)
      .then((list) => alive && setZones(list))
      // No permission to see zones, or none on this server: the replay works without them.
      .catch(() => alive && setZones([]));
    return () => {
      alive = false;
    };
  }, [winFrom, winTo]);

  useEffect(() => saveZonePrefs(prefs), [prefs]);

  const shapes = useMemo(() => zoneShapes(zones), [zones]);
  const types = useMemo(() => presentZoneTypes(zones), [zones]);
  const visible = shapesAt(shapes, at, prefs.types);
  const visibleKey = visible.map((s) => s.key).join('|');
  // Rebuilt only when the set changes (a version starts or ends at the clock, a type toggles).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const drawn = useMemo(() => visible, [visibleKey]);
  const fc = useMemo(() => shapesCollection(drawn), [drawn]);

  const toggle = useCallback(() => setPrefs((p) => ({ ...p, on: !p.on })), []);
  const toggleType = useCallback(
    (id: string) =>
      setPrefs((p) => ({ ...p, types: { ...p.types, [id]: p.types[id] === false } })),
    []
  );

  const available = shapes.length > 0;
  const layer = available && prefs.on ? <ReplayZoneLayer fc={fc} shapes={drawn} /> : null;

  const legend =
    available && prefs.on && types.length > 0 ? (
      <li key="zones" className="inline-flex flex-wrap items-center gap-1.5" role="group" aria-label="Zone layers">
        <span>Zones:</span>
        {types.map((t) => {
          const isOn = prefs.types[t.id] !== false;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => toggleType(t.id)}
              aria-pressed={isOn}
              title={isOn ? `Hide ${t.label.toLowerCase()}` : `Show ${t.label.toLowerCase()}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                isOn
                  ? 'bg-surface text-slate-700 border-slate-300'
                  : 'bg-slate-100 text-slate-400 border-transparent hover:text-slate-600'
              }`}
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={
                  isOn
                    ? { background: `${t.color}33`, boxShadow: `inset 0 0 0 1.5px ${t.color}` }
                    : { background: 'var(--border-strong)' }
                }
              />
              {t.label}
            </button>
          );
        })}
      </li>
    ) : null;

  return { available, on: prefs.on, toggle, layer, legend };
}
